import { Status, StatusDropdown } from "components";
import { useFreefieldState } from "shared/utils/freeFields/hooks";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import { CompanySidePanel } from "components/SidePanel/Company/CompanySidePanel";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import moment from "moment";
import { Message } from "semantic-ui-react";
import { ProductionDateRange } from "Production/components/ProductionDateRange";
import React, { useState } from "react";
import { freeFieldEditableSections } from "show/components/freeFields/freeFieldEditableSections";
import { getFreefieldId, isFreefieldKey } from "shared/utils/freeFields/utils";
import { useHasMultipleStatuses } from "components/Status/hooks";
import { getTourFieldMetaData, getUpdateProductionInstruction, mapTourToState, TourFormState } from "shared/utils/fieldsHelper/tourFields";

interface ProductionDetailSectionProps {
  production: server.dto.Tour;
  ffCategories: server.dto.FreeFieldCategorySmall[];
  ffPermissions: server.dto.FreeFieldDefinitionPermission[];
}

export const ProductionDetailSection = (props: ProductionDetailSectionProps) => {
  const production = props.production;
  const hasMultipleStatuses = useHasMultipleStatuses("tour", production.clientId);
  const [formState, setFormState] = useState<TourFormState>(mapTourToState(production));
  const fieldMetaData = getTourFieldMetaData(site.currentEnvironment, formState, production.permissions, hasMultipleStatuses);

  const ffState = useFreefieldState(production.freeFields, props.ffCategories, props.ffPermissions);

  const urlInputs = new Set(props.ffCategories.map(ffCategory => ffCategory.definitions.map(definition => {
    if (definition.type === "url") {
      return `${definition.freeFieldDefinitionId}`;
    }
    return undefined;
  })).flat().filter(x => x !== undefined));

  const hasArtistAndDate = production.artists.length > 0 && production.startDate !== null && production.endDate !== null;
  const startMoment = moment.utc(props.production.startDate);
  const endMoment = moment.utc(props.production.endDate);

  const onValueChanged = (key: string, value: any) => {
    if (key === "name") {
      setFormState(prevState => ({
        ...prevState,
        name: value
      }));
    } else if (key === "status") {
      setFormState(prevState => ({
        ...prevState,
        status: value
      }));
    } else if (isFreefieldKey(key)) {
      ffState.setEditableFreefieldValue(getFreefieldId(key), value);
    } else {
      console.warn(`Unknown field key ${key} with value ${value} was changed`);
    }
  }

  const onSuccessfulUpdate = async (key: string, newValue: any) => {
    // Reformat URL if it's a free field URL input
    if (urlInputs.has(key)) {
      const result = await window.CoreApi.Url.Reformat(newValue);
      ffState.setEditableFreefieldValue(parseInt(key), result.data.data);
    }
  }

  return <DetailSectionTemplate
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={onSuccessfulUpdate}
    isInPageEditable={production.permissions.canModify}
    sections={[{
      key: "productionMain",
      type: "regular",
      title: site.currentEnvironment.tour.singular,
      fields: [{
        ...fieldMetaData.name.fields(),
        key: "name",
        label: RESX.GeneralLabels.Name,
        render: () => <div>{formState.name}</div>,
        inPageEdit: {
          type: "confirmOrCancelButton",
          initialValue: formState.name,
          fullWidth: true,
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => (
            <RequiredTextInput
              validatorKey="name"
              focusOnMount
              maxLength={serverReference.constants.tour.maxNameLength}
              value={value}
              onChange={(name) => { onChange(name); }}
              onEnter={() => submit()}
              onEsc={() => cancel()}
            />
          ),
          submitChange: (name: string) => window.CoreApi.Production.Update(
            getUpdateProductionInstruction({ ...formState, name }))
        }
      }, {
        ...fieldMetaData.status.fields(),
        key: "status",
        label: RESX.Status.resxStatus,
        render: () => <Status status={formState.status} />,
        inPageEdit: {
          render: ({ value, onChange }) => (
            <StatusDropdown
              value={value}
              onChange={onChange}
              mainArea="tour"
              clientId={production.clientId}
              options={undefined}
              config={{
                focusOnMount: true,
                openMenuOnFocus: true
              }}
            />
          ),
          type: "custom",
          initialValue: formState.status,
          fullWidth: true,
          submitChange: (status: server.dto.StatusSmall) => window.CoreApi.Production.Update(
            getUpdateProductionInstruction({ ...formState, status }))
        }
      }, {
        key: "date",
        label: RESX.DateTime.lblDate,
        hidden: !hasArtistAndDate,
        render: () => <ProductionDateRange startDate={startMoment} endDate={endMoment} />
      }, {
        key: "artist",
        label: site.currentEnvironment.functionGroups.artist.plural,
        hidden: production.artists.length === 0,
        render: () => <>
          {production.artists.map(artist =>
            <div key={artist.artistId}>
              <CompanySidePanel type="artist"
                companyId={artist.artistId}
                trigger={<a>{artist.name}</a>}
              />
            </div>
          )}
        </>
      }, {
        key: "noShowsMessage",
        hidden: hasArtistAndDate,
        hideLabel: true,
        label: "",
        render: () => <Message color="blue">{RESX.Tour.NoAssociatedShowsMessage}</Message>
      }]
    },
    ...freeFieldEditableSections(
      production.productionId,
      ffState.freefieldCategoriesWithValuesAndPermissions,
      window.CoreApi.Production.UpdateFreeField,
    )]}
  />
};